/**
 * ChainStepFields Component
 *
 * Step 2 of the approval flow: the select naming how the approver is found and the pick that
 * chain needs (a position, a role, or the user of the user chain).
 */
import { Link } from 'react-router-dom';
import { AlertBanner, InputWrapper, Select } from '@insio/ui-toolkit';
import { ApproverFallbackStrategy, Permission, UserRole } from '@insio/shared-types';
import { usePermissions } from '@/features/auth';
import { navigation } from '@/navigation';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useAnyApproverHint } from '@/features/procurement/hooks/use-any-approver-hint';
import {
  fallbackIssueOf,
  missingPickOf,
  requiresUser,
  usesOrgStructure,
  usesOrgUnit,
  usesRole,
  withStrategy,
  type FallbackDraft,
  type FallbackIssue,
  type FallbackUser
} from './approval-flow';

/** Every role an account member may hold; the platform's own roles are not offered. */
const ACCOUNT_ROLES: readonly UserRole[] = Object.values(UserRole).filter(
  role => !role.startsWith('SYSTEM_')
);

/** From the most general answer to the most specific one, as the select lists them. */
const STRATEGY_ORDER: readonly ApproverFallbackStrategy[] = [
  ApproverFallbackStrategy.MANUAL_APPROVER_SELECTION,
  ApproverFallbackStrategy.ANYONE_WITH_PERMISSION,
  ApproverFallbackStrategy.ORG_STRUCTURE_THEN_FALLBACK_USER_THEN_ANYONE_WITH_PERMISSION,
  ApproverFallbackStrategy.DOCUMENT_UNIT_MANAGER_THEN_ANYONE_WITH_PERMISSION,
  ApproverFallbackStrategy.ORG_UNIT_MANAGER_THEN_ANYONE_WITH_PERMISSION,
  ApproverFallbackStrategy.ORG_UNIT_MEMBER_THEN_ANYONE_WITH_PERMISSION,
  ApproverFallbackStrategy.ROLE_MEMBER_THEN_ANYONE_WITH_PERMISSION,
  ApproverFallbackStrategy.FALLBACK_USER_THEN_ANYONE_WITH_PERMISSION,
  ApproverFallbackStrategy.SUGGESTED_APPROVER_THEN_ANYONE_WITH_PERMISSION
];

export interface FallbackIssueBannerProps {
  /** Feature translation prefix of the `fallback` subtree. */
  translationPrefix: string;
  issue: FallbackIssue;
  testIdPrefix: string;
}

/** Why the picked chain cannot be stored. */
export function FallbackIssueBanner({
  translationPrefix,
  issue,
  testIdPrefix
}: FallbackIssueBannerProps) {
  const t = useFeatureTranslation(translationPrefix);
  // Roles are changed in the user settings; only whoever may manage users is sent there.
  const { hasPermission } = usePermissions();
  const canManageUsers = hasPermission(Permission.USERS_MANAGE);
  return (
    <AlertBanner
      variant='error'
      title={t(`issue.${issue.kind}`)}
      body={
        issue.kind === 'USER_CANNOT_APPROVE' && canManageUsers ? (
          <>
            {t('issue.body')}{' '}
            <Link
              to={navigation.settings('users')}
              className='underline hover:opacity-70'
              data-testid={`${testIdPrefix}-issue-users-link`}
            >
              {t('issue.usersLink')}
            </Link>
          </>
        ) : (
          t('issue.body')
        )
      }
      testId={`${testIdPrefix}-issue`}
    />
  );
}

export interface ChainStepFieldsProps {
  /** Feature translation prefix of the `fallback` subtree. */
  translationPrefix: string;
  draft: FallbackDraft;
  onChange: (draft: FallbackDraft) => void;
  /** Everyone who may approve this kind; undefined until known, when no pick is flagged. */
  users: readonly FallbackUser[] | undefined;
  /** Chains this document kind can follow; all of them when the caller does not narrow it. */
  strategies?: readonly ApproverFallbackStrategy[];
  orgUnits: readonly { id: string; name: string }[];
  /** Roles the backend accepts for the role chain — those granting the approve permission. */
  roles: readonly UserRole[];
  approvePermission: Permission;
  /** Locks every pick, e.g. while those who may approve are not known. */
  disabled?: boolean;
  testIdPrefix: string;
}

export function ChainStepFields({
  translationPrefix,
  draft,
  onChange,
  users,
  strategies,
  orgUnits,
  roles,
  approvePermission,
  disabled = false,
  testIdPrefix
}: ChainStepFieldsProps) {
  const t = useFeatureTranslation(translationPrefix);
  const tStrategy = useEnumTranslation('approverFallbackStrategy');
  const tRole = useEnumTranslation('userRole');
  // The order is the component's; which of them a document kind can follow is the server's.
  const offered = STRATEGY_ORDER.filter(
    strategy => strategies === undefined || strategies.includes(strategy)
  );
  const anyApproverHint = useAnyApproverHint(approvePermission);
  const issue = fallbackIssueOf(draft, { users, eligibleRoles: roles, approvePermission });
  // A fallback user who may not approve is step 3's issue, unless the user chain makes it step 2.
  const stepIssue =
    issue && (issue.kind !== 'USER_CANNOT_APPROVE' || requiresUser(draft.strategy)) ? issue : null;
  const missingPick = missingPickOf(draft);

  return (
    <div className='flex flex-col gap-md'>
      <InputWrapper label={t('strategyLabel')} className='gap-xs'>
        <Select
          value={draft.strategy}
          onChange={value => onChange(withStrategy(draft, value as ApproverFallbackStrategy))}
          options={offered.map(strategy => ({
            value: strategy,
            label: tStrategy(strategy)
          }))}
          disabled={disabled}
          testId={`${testIdPrefix}-strategy`}
        />
      </InputWrapper>

      {usesOrgUnit(draft.strategy) && (
        <InputWrapper label={t('orgUnitLabel')} className='gap-xs lg:w-96'>
          <Select
            value={draft.orgUnitId}
            onChange={value => onChange({ ...draft, orgUnitId: String(value ?? '') })}
            options={orgUnits.map(unit => ({ value: unit.id, label: unit.name }))}
            UnselectedOption={t('orgUnitPlaceholder')}
            isSearchable
            disabled={disabled}
            testId={`${testIdPrefix}-org-unit`}
          />
        </InputWrapper>
      )}

      {usesRole(draft.strategy) && (
        <InputWrapper label={t('roleLabel')} className='gap-xs lg:w-96'>
          <Select
            value={draft.role}
            onChange={value => onChange({ ...draft, role: String(value ?? '') })}
            options={ACCOUNT_ROLES.map(role => ({ value: role, label: tRole(role) }))}
            UnselectedOption={t('rolePlaceholder')}
            disabled={disabled}
            testId={`${testIdPrefix}-role`}
          />
        </InputWrapper>
      )}

      {requiresUser(draft.strategy) && (
        <InputWrapper label={t('userLabel')} className='gap-xs lg:w-96'>
          <Select
            value={draft.userId}
            onChange={value => onChange({ ...draft, userId: String(value ?? '') })}
            options={(users ?? []).map(user => ({ value: user.id, label: user.displayName }))}
            UnselectedOption={t('userPlaceholder')}
            isSearchable
            disabled={disabled}
            testId={`${testIdPrefix}-user`}
          />
        </InputWrapper>
      )}

      {missingPick && (
        <AlertBanner
          variant='warning'
          title={t(`missingChoice.${missingPick}`)}
          testId={`${testIdPrefix}-missing-choice`}
        />
      )}

      {stepIssue && (
        <FallbackIssueBanner
          translationPrefix={translationPrefix}
          issue={stepIssue}
          testIdPrefix={testIdPrefix}
        />
      )}

      <p className='text-sm text-foreground-muted' data-testid={`${testIdPrefix}-description`}>
        {t(`strategies.${draft.strategy}`)}
        {draft.strategy === ApproverFallbackStrategy.ANYONE_WITH_PERMISSION &&
          ` ${anyApproverHint}`}
        {usesOrgStructure(draft.strategy) && (
          <>
            {' '}
            <Link
              to={navigation.codeLists('org-structure')}
              className='underline hover:opacity-70'
              data-testid={`${testIdPrefix}-org-structure-link`}
            >
              {t('orgStructureLink')}
            </Link>
          </>
        )}
      </p>
    </div>
  );
}
